import * as React from "react"
import { useShowSuccessToast } from "gatsby-interface"
import { CdnVendor } from "@modules/graphql/types"
import {
  gatsbyCloudCdnIntegrationConfig as text,
  errorAlert as errorText,
} from "@modules/locales/default.js"
import { SegmentEventType, useTracker } from "@modules/analytics"
import { useTriggerErrorAlert } from "@modules/ui/components/ErrorAlert"
import { useSupportedCdnIntegrationsQuery } from "@modules/cdn/shared/queries.generated"
import { useConnectCloudCdn } from "../components/GatsbyCloudCdn.helpers"
import { useHostingDetailsForSiteQuery } from "@modules/cdn/gatsbyCloudCdn/queries.generated"

export type UseCloudCdnToggleProps = {
  siteId: string
  onEnabledSuccess?: () => void
  onEnabledFailed?: () => void
  onDisabledSuccess?: () => void
  onDisabledFailed?: () => void
}

export enum ConnectState {
  Connected = `CONNECTED`,
  NotConnected = `NOT_CONNECTED`,
  Connecting = `CONNECTING`,
}

type SimpleError = {
  message: string
}

export function useCloudCdnToggle({
  siteId,
  onEnabledSuccess,
  onEnabledFailed,
  onDisabledSuccess,
  onDisabledFailed,
}: UseCloudCdnToggleProps) {
  const {
    connectCloudCdn,
    disconnectCloudCdn,
    isUpdatingCloudCdn,
  } = useConnectCloudCdn(siteId)
  const { trackSegment, trackAction } = useTracker()
  const showSuccessToast = useShowSuccessToast()
  const [setError, errorAlert] = useTriggerErrorAlert()

  const {
    data: cdnIntegrationsData,
    loading: cdnIntegrationLoading,
  } = useSupportedCdnIntegrationsQuery({
    variables: { siteId },
    onError: setError,
  })
  const cdnIntegrations = cdnIntegrationsData?.supportedCDNIntegrations ?? []
  const isConnectedToGatsbyCdn = cdnIntegrations.some(
    ({ vendor, connected }) =>
      vendor === CdnVendor.CloudCdn && connected === true
  )

  const [connectState, setConnectState] = React.useState<ConnectState>(
    isConnectedToGatsbyCdn ? ConnectState.Connected : ConnectState.NotConnected
  )
  const isConnected = connectState === ConnectState.Connected

  const {
    data: hostingDetails,
    loading: hostingDetailsLoading,
  } = useHostingDetailsForSiteQuery({
    variables: { siteId },
    pollInterval: 15000,
    onError: setError,
    skip: !isConnectedToGatsbyCdn,
  })
  const hostingDetailsForSite = hostingDetails?.hostingDetailsForSite?.[0]

  React.useEffect(() => {
    setConnectState(
      isConnectedToGatsbyCdn
        ? ConnectState.Connected
        : ConnectState.NotConnected
    )
  }, [cdnIntegrationsData])

  const enableCloudCdn = async () => {
    setConnectState(ConnectState.Connecting)
    const result = await connectCloudCdn()
    try {
      if (result?.success) {
        setConnectState(ConnectState.Connected)
        onEnabledSuccess?.()
        trackAction({
          eventType: `TRACK_EVENT`,
          name: `Gatsby Hosting Enabled`,
          uiSource: `Site Settings`,
        })
        trackSegment({
          type: SegmentEventType.Track,
          event: "Gatsby Hosting enabled",
          properties: {
            location: "Site Settings",
          },
        })
        return showSuccessToast(text.messages.gatsbyCloudTurnedOn)
      }

      setConnectState(ConnectState.NotConnected)

      const errMessage =
        result?.message || errorText.messages.somethingWentWrong
      onEnabledFailed?.()
      return setError({ message: errMessage })
    } catch (err) {
      setConnectState(ConnectState.NotConnected)
      setError({ message: (err as SimpleError).message })
    }
  }

  const disableCloudCdn = async () => {
    try {
      const result = await disconnectCloudCdn()
      if (result?.success) {
        setConnectState(ConnectState.NotConnected)
        onDisabledSuccess?.()
        trackAction({
          eventType: `TRACK_EVENT`,
          name: `Gatsby Hosting Disabled`,
          uiSource: `Site Settings`,
        })
        trackSegment({
          type: SegmentEventType.Track,
          event: "Gatsby Hosting disabled",
          properties: {
            location: "Site Settings",
          },
        })
        return showSuccessToast(text.messages.gatsbyCloudTurnedOff)
      }

      const errMessage =
        result?.message || errorText.messages.somethingWentWrong
      return setError({ message: errMessage })
    } catch (err) {
      onDisabledFailed?.()
      setError({ message: (err as SimpleError).message })
    }
  }
  return {
    isConnected,
    isUpdatingCloudCdn,
    isConnectedToGatsbyCdn,
    cdnIntegrationLoading,
    hostingDetailsForSite,
    hostingDetailsLoading,
    errorAlert,
    connectState,
    cdnIntegrations,
    setConnectState,
    setError,
    enableCloudCdn,
    disableCloudCdn,
  }
}
